import { Ionicons } from '@expo/vector-icons';
import { router, usePathname } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { TouchableOpacity, View, Text, Linking, Platform, StyleProp, ViewStyle, TextStyle } from 'react-native';

import { cardStyles } from '@/styles';
import { extractShortcode, findAccountForShortcode, replaceShortcodes, ShortcodeAccountInfo } from '@/utils/shortcode-lookup';

interface OpenInMapsProps {
	address?: string | null;
	latitude?: number | null;
	longitude?: number | null;
	children?: React.ReactNode;
	style?: StyleProp<ViewStyle>;
	textStyle?: StyleProp<TextStyle>;
	distanceKm?: number;
	associationId?: string;
	/**
	 * When true, renders the raw shortcode (e.g. "4769SA") next to the
	 * expanded address; if an account in this association uses the same
	 * shortcode, the chip becomes a link to that account.
	 */
	showAccountLink?: boolean;
}

export const OpenInMaps: React.FC<OpenInMapsProps> = ({
	address,
	latitude,
	longitude,
	children,
	style,
	textStyle,
	distanceKm,
	associationId,
	showAccountLink,
}) => {
	const [expandedAddress, setExpandedAddress] = useState<string | null>(address || null);
	const [accountInfo, setAccountInfo] = useState<ShortcodeAccountInfo | null>(null);
	const shortcode = showAccountLink ? extractShortcode(address) : null;
	const accountId = accountInfo?.accountId ?? null;
	const hasLocation = (latitude && longitude) || address;
	const currentPath = usePathname();

	useEffect(() => {
		if (address && associationId) {
			replaceShortcodes(address, associationId).then(setExpandedAddress);
		}
	}, [address, associationId]);

	useEffect(() => {
		if (!showAccountLink || !address || !associationId) {
			setAccountInfo(null);
			return;
		}
		findAccountForShortcode(address, associationId).then((match) => {
			setAccountInfo(match ? { accountId: match.accountId, accountNumber: match.accountNumber, ownerLabel: match.ownerLabel, address: match.address } : null);
		});
	}, [showAccountLink, address, associationId]);

	const openInMaps = () => {
		let url = '';
		if (Platform.OS === 'ios') {
			if (latitude && longitude) {
				url = `http://maps.apple.com/?ll=${latitude},${longitude}`;
			} else if (address) {
				url = `http://maps.apple.com/?q=${encodeURIComponent(expandedAddress || address)}`;
			}
		} else {
			if (latitude && longitude) {
				url = `https://maps.google.com/maps?q=${latitude},${longitude}`;
			} else if (address) {
				url = `https://maps.google.com/maps?q=${encodeURIComponent(expandedAddress || address)}`;
			}
		}
		if (url) {
			Linking.openURL(url);
		}
	};

	if (!hasLocation) {
		return (
			<View style={[cardStyles.location, style]}>
				<Text style={[cardStyles.locationText, textStyle]} numberOfLines={1}>
					No location
				</Text>
			</View>
		);
	}
	if (address?.toLowerCase() === 'various') {
		return (
			<View style={[cardStyles.location, style]}>
				<Text style={[cardStyles.locationText, textStyle, {
					fontStyle: 'italic',
					color: '#666',
					textDecorationStyle: 'solid',
					textDecorationLine: 'none',
				}]} numberOfLines={1}>
					Various
				</Text>
			</View>
		);
	}

	if (showAccountLink && shortcode) {
		const subtitle = accountInfo?.ownerLabel || accountInfo?.accountNumber;
		const firstLine = (
			<Text style={[cardStyles.locationText, textStyle, { color: '#000' }]} numberOfLines={1}>
				<Text style={{ fontWeight: '600' }}>{shortcode}</Text>
				{subtitle ? <Text>{` — ${subtitle}`}</Text> : null}
			</Text>
		);
		return (
			<View style={[{ paddingTop: 0, paddingBottom: 10 }, style]}>
				{accountId ? (
					<TouchableOpacity
						onPress={() => router.push({
							pathname: '/accounting/account/[id]',
							params: { id: accountId, from: currentPath ?? '' },
						})}
						accessibilityRole='link'
					>
						{firstLine}
					</TouchableOpacity>
				) : firstLine}
				<TouchableOpacity onPress={openInMaps} accessibilityRole='button'>
					<Text style={[cardStyles.locationText, textStyle]} numberOfLines={1}>
						{children || expandedAddress || address}
					</Text>
				</TouchableOpacity>
			</View>
		);
	}

	return (
		<TouchableOpacity
			style={[cardStyles.location, style]}
			onPress={openInMaps}
			accessibilityRole='button'
		>
			<Text style={[cardStyles.locationText, textStyle]} numberOfLines={1}>
				{children || expandedAddress || address || (
					<Ionicons name="location" size={16} style={{ color: '#666' }} />
				)}
			</Text>
			{typeof distanceKm === 'number' && (
				<View style={cardStyles.mapDistance}>
					<Text style={{ textAlign: 'right', fontSize: 12, color: '#888', marginTop: 2 }}>
						{(distanceKm * 0.621371).toFixed(1)} mi away
					</Text>
					<Ionicons name='location-outline' size={16} style={cardStyles.locationIcon} />
				</View>
			)}
		</TouchableOpacity>
	);
};
